import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { EditIcon } from "../../../../../assets/images/icons/EditIcon";
import BriaTooltip from "../../../../../components/common/BriaTooltip/BriaTooltip";
import { useAppStore } from "../../../../../hooks/useStores";
import { VideoEditingSolidBgColor } from "../../../../../models/video-editing";
import styles from "./VideoSolidBackgroundConfig.module.scss";

const VideoSolidBackgroundConfig = () => {
	const { videoEditingStore } = useAppStore();
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.config.tools.solid_background.config" });
	const selectedColor = videoEditingStore.config.tool_config?.solid_background?.background_color;

	const solidBgColors = Object.keys(t("options", { returnObjects: true })) as VideoEditingSolidBgColor[];

	const handleColorChange = (color?: VideoEditingSolidBgColor) => {
		videoEditingStore.handleConfigChange("tool_config", {
			...videoEditingStore.config.tool_config,
			solid_background: {
				background_color: color,
			},
		});
	};

	const ColorAppliedComponent = ({ color, size }: { color: VideoEditingSolidBgColor; size?: number }) => {
		return (
			<Box className={styles.colorAppliedComponent}>
				<Box className={styles.selectedColor}>
					<ColorOption color={color} size={size} disablePointerEvents />
					<Box className={styles.description}>{t("hasColorDescription", { color: color })}</Box>
				</Box>
				<EditIcon onClick={() => handleColorChange(undefined)} className={styles.iconButton} />
			</Box>
		);
	};

	return (
		<Box className={styles.container}>
			{selectedColor ? (
				<ColorAppliedComponent color={selectedColor} size={26} />
			) : (
				<Box className={styles.colorPicker}>
					<Box className={styles.description}>{t("description")}</Box>
					<Box className={styles.colorOptions}>
						{solidBgColors.map((color) => (
							<ColorOption key={color} color={color} onClick={handleColorChange} />
						))}
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default observer(VideoSolidBackgroundConfig);

export const ColorOption = ({
	color,
	size,
	disablePointerEvents,
	onClick,
}: {
	color?: VideoEditingSolidBgColor;
	size?: number;
	disablePointerEvents?: boolean;
	onClick?: (color?: VideoEditingSolidBgColor) => void;
}) => {
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.config.tools.solid_background.config" });
	return (
		<BriaTooltip disable={disablePointerEvents} title={t(`options.${color}`)}>
			<Box
				className={styles.colorOption}
				style={{
					width: size,
					height: size,
					backgroundColor: color || "transparent",
					pointerEvents: disablePointerEvents ? "none" : "auto",
				}}
				onClick={() => onClick?.(color)}
			/>
		</BriaTooltip>
	);
};
